import { EventStatus } from '@/feature/bets-sbk/hooks/types';
import { renderDate } from '@/utils/renderDate';

/**
 * Whether an event is currently live, read from the event object's own embedded
 * `event_details.status`, falling back to the top-level `status`.
 *
 * `event_details.status` is preferred because the top-level `status` is captured
 * once when a selection is added and never re-flips, whereas `event_details` is
 * refreshed in place by live socket updates (in the betslip store, via
 * `ScoreboardUpdate` match-update messages — see `use-subscribe-events.ts`).
 *
 * This reads the embedded status only. Callers that hold an event id and the global
 * live cache (`useEventDetailsCache`) should derive liveness via `useEventStatus` /
 * `useIsMatchupLive` instead, which read that cache directly.
 */
export const isEventLive = (event: {
    status: EventStatus;
    // `string`, not `EventStatus`: the feed sends lowercase (e.g. `'live'`), which is
    // why we `.toUpperCase()` — typing the truth here avoids casts at the callers.
    event_details?: { status?: string };
}): boolean => (event.event_details?.status?.toUpperCase() ?? event.status) === 'LIVE';

/**
 * Resolves the time-slot string for an event.
 * - If period/clock data is available, use it (e.g. "1st, 8:37").
 * - Otherwise, a live event shows `liveLabel`; an upcoming event shows its
 *   formatted start time.
 * `liveLabel` is injected (the translated `event_time:live` copy) so this stays a
 * pure, i18n-free util — callers, which are hooks, own the `t()` call.
 */
export const resolveEventTimeInfo = ({
    periodTime,
    isLive,
    startTime,
    liveLabel,
}: {
    periodTime: string | undefined;
    isLive: boolean;
    startTime: number;
    liveLabel: string;
}): string => periodTime || (isLive ? liveLabel : renderDate(new Date(startTime)));
